import { useEffect, useMemo, useState } from 'react';
import {
  FiUsers,
  FiEdit2,
  FiFilter,
  FiHash,
  FiSearch,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiFolder,
  FiDollarSign,
  FiCreditCard,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import EntityFilesModal from '../../../components/EntityFilesModal';
import EmployeeBenefitsModal from '../../../components/EmployeeBenefitsModal';
import EmployeeBankDataModal from '../../../components/EmployeeBankDataModal';
import Api from '../../../Api';
import {
  Button,
  Input,
  Modal,
  Skeletons,
  Table,
  TableActions,
  EmployeeForm,
  Tooltip,
} from '../../../components';
import { createEmployeeColumns } from '../../EmployeeManager/employeeColumns';
import '../../CompanyManager/style.css';

export default function LinkedEmployeesModal({ isOpen, onClose, station, company, companies = [] }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Sub-modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [employeeToActivate, setEmployeeToActivate] = useState(null);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [filesCategory, setFilesCategory] = useState('geral');
  const [selectedEmployeeForFiles, setSelectedEmployeeForFiles] = useState(null);
  
  const [benefitsModalOpen, setBenefitsModalOpen] = useState(false);
  const [bankDataModalOpen, setBankDataModalOpen] = useState(false);
  const [selectedEmployeeForActions, setSelectedEmployeeForActions] = useState(null);

  useEffect(() => {
    if (isOpen && station && company) {
      loadLinkedEmployees();
    }
  }, [isOpen, station, company]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  async function loadLinkedEmployees() {
    try {
      setLoading(true);
      // Fetch employees linked to this station
      const data = await Api.getServiceStationEmployees(company.id, station.id);

      if (Array.isArray(data)) {
        setEmployees(data);
      } else {
        setEmployees([]);
        // Don't set error if just empty, but here if data is not array it might be an issue
        // Api.getServiceStationEmployees might return empty array if none
      }
    } catch (err) {
      setError('Erro ao carregar funcionários vinculados.');
      toast.error('Erro ao carregar funcionários vinculados.');
    } finally {
      setLoading(false);
    }
  }

  // Action Handlers
  function handleEdit(employee) {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  }

  function handleFormSuccess() {
    loadLinkedEmployees();
    setIsEditModalOpen(false);
    setSelectedEmployee(null);
  }

  function handleRequestDelete(employee) {
    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  }

  function handleRequestActivate(employee) {
    setEmployeeToActivate(employee);
    setIsActivateModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!employeeToDelete || !company) return;

    try {
      setIsDeleting(true);
      const result = await Api.deleteEmployee(company.id, employeeToDelete.id);

      if (result && !result.error) {
        setEmployees(prev => prev.map(e => e.id === employeeToDelete.id ? { ...e, is_active: false } : e));
        setIsDeleteModalOpen(false);
        setEmployeeToDelete(null);
        toast.success('Funcionário inativado com sucesso!');
      } else {
        toast.error(result?.error || 'Erro ao inativar funcionário.');
      }
    } catch (err) {
      toast.error('Erro ao inativar funcionário.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleConfirmActivate() {
    if (!employeeToActivate || !company) return;

    try {
      setIsActivating(true);
      const result = await Api.activateEmployee(company.id, employeeToActivate.id);

      if (result && !result.error) {
        setEmployees(prev => prev.map(e => e.id === employeeToActivate.id ? { ...e, is_active: true } : e));
        setIsActivateModalOpen(false);
        setEmployeeToActivate(null);
        toast.success('Funcionário ativado com sucesso!');
      } else {
        toast.error(result?.error || 'Erro ao ativar funcionário.');
      }
    } catch (err) {
      toast.error('Erro ao ativar funcionário.');
    } finally {
      setIsActivating(false);
    }
  }

  function handleFiles(employee) {
    setSelectedEmployeeForFiles(employee);
    setFilesCategory('geral');
    setFilesModalOpen(true);
  }

  function handlePointFiles(employee) {
    setSelectedEmployeeForFiles(employee);
    setFilesCategory('ponto');
    setFilesModalOpen(true);
  }

  function handleBenefits(employee) {
    setSelectedEmployeeForActions(employee);
    setBenefitsModalOpen(true);
  }

  function handleBankData(employee) {
    setSelectedEmployeeForActions(employee);
    setBankDataModalOpen(true);
  }

  function handleBenefitsSuccess() {
    loadLinkedEmployees();
    setBenefitsModalOpen(false);
    setSelectedEmployeeForActions(null);
  }

  function handleBankDataSuccess() {
    loadLinkedEmployees();
    setBankDataModalOpen(false);
    setSelectedEmployeeForActions(null);
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  }

  function handleCancelActivate() {
    if (isActivating) return;
    setIsActivateModalOpen(false);
    setEmployeeToActivate(null);
  }

  // Filtering and Pagination
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return employees.filter(employee => {
      const matchesSearch =
        !term ||
        (employee.name || '').toLowerCase().includes(term) ||
        (employee.email || '').toLowerCase().includes(term) ||
        (employee.cpf || '').toLowerCase().includes(term) ||
        (employee.registration_number || '').toLowerCase().includes(term);

      let matchesStatus = true;

      if (statusFilter === 'active') {
        matchesStatus = employee.is_active === true;
      } else if (statusFilter === 'inactive') {
        matchesStatus = employee.is_active === false;
      }

      return matchesSearch && matchesStatus;
    });
  }, [employees, searchTerm, statusFilter]);

  const totalItems = filteredEmployees.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = createEmployeeColumns({
    onEdit: handleEdit,
    onFiles: handleFiles,
    onPointFiles: handlePointFiles,
    onBenefits: handleBenefits,
    onBankData: handleBankData,
    onRequestDelete: handleRequestDelete,
    onRequestActivate: handleRequestActivate,
    showCompanyColumn: false,
    showStationsColumn: false,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Funcionários Vinculados - ${station?.name || ''}`}
      size="xl"
      closeOnOverlayClick={false}
    >
      <div className="manager-content" style={{ padding: '1.5rem' }}>
        <div className="manager-filters-card">
          <div className="manager-filters-grid-two" style={{ gridTemplateColumns: '1fr 1fr !important' }}>
            <div className="manager-filter-group">
              <label className="manager-filter-label">Buscar</label>
              <div className="manager-filter-search">
                <Input
                  id="employee-search"
                  placeholder="Busque por nome, e-mail, CPF ou matrícula"
                  value={searchTerm}
                  onChange={event =>
                    setSearchTerm(event.target.value)
                  }
                  icon={FiSearch}
                />
              </div>
            </div>

            <div className="manager-filter-group">
              <label className="manager-filter-label">Status</label>
              <div className="manager-filter-chips">
                <button
                  type="button"
                  className={[
                    'manager-chip',
                    statusFilter === 'all' && 'manager-chip-active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setStatusFilter('all')}
                >
                  Todos
                </button>
                <button
                  type="button"
                  className={[
                    'manager-chip',
                    statusFilter === 'active' && 'manager-chip-active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setStatusFilter('active')}
                >
                  Ativos
                </button>
                <button
                  type="button"
                  className={[
                    'manager-chip',
                    statusFilter === 'inactive' && 'manager-chip-active',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setStatusFilter('inactive')}
                >
                  Inativos
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && <Skeletons.CompanyManagerSkeleton />}

        {!loading && employees.length === 0 && (
          <div className="empty-state">
            <p>Nenhum funcionário vinculado a este posto.</p>
          </div>
        )}

        {!loading && employees.length > 0 && (
          <Table
            items={paginatedEmployees}
            columns={columns}
            totalItems={totalItems}
            page={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={value => {
              setPageSize(value);
              setCurrentPage(1);
            }}
            getRowKey={employee => employee.id}
            emptyMessage="Nenhum funcionário encontrado com os filtros atuais."
            onRowDoubleClick={(employee) => handleEdit(employee)}
          />
        )}
      </div>

      {/* Sub-modals */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={selectedEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
        closeOnOverlayClick={false}
        size='xl'
      >
        <EmployeeForm
          companyId={company ? company.id : null}
          initialData={selectedEmployee}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsEditModalOpen(false)}
          companies={companies}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar inativação"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja inativar o funcionário{' '}
            {employeeToDelete && (
              <span className="delete-modal-company-name">
                {employeeToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>O funcionário não terá mais acesso ao sistema, mas seus dados históricos serão mantidos.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Inativando...' : 'Inativar'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isActivateModalOpen}
        onClose={handleCancelActivate}
        title="Ativar Funcionário"
        width="400px"
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja ativar o funcionário{' '}
            <strong>{employeeToActivate?.name}</strong>?
          </p>
          <div className="delete-modal-actions">
            <Button
              variant="secondary"
              onClick={handleCancelActivate}
              disabled={isActivating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmActivate}
              disabled={isActivating}
            >
              {isActivating ? 'Ativando...' : 'Ativar'}
            </Button>
          </div>
        </div>
      </Modal>

      <EntityFilesModal
        isOpen={filesModalOpen}
        onClose={() => {
          setFilesModalOpen(false);
          setSelectedEmployeeForFiles(null);
          setFilesCategory('geral');
        }}
        entity={selectedEmployeeForFiles}
        entityType="employee"
        company={selectedEmployeeForFiles?.company || company}
        category={filesCategory}
      />

      <EmployeeBenefitsModal
        isOpen={benefitsModalOpen}
        onClose={() => setBenefitsModalOpen(false)}
        companyId={company?.id}
        employee={selectedEmployeeForActions}
        onSuccess={handleBenefitsSuccess}
      />

      <EmployeeBankDataModal
        isOpen={bankDataModalOpen}
        onClose={() => setBankDataModalOpen(false)}
        companyId={company?.id}
        employee={selectedEmployeeForActions}
        onSuccess={handleBankDataSuccess}
      />
    </Modal>
  );
}
